import { Link } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Instagram, Facebook, Mail, MapPin, Phone, Loader2 } from "lucide-react";
import EmailLink from "@/components/EmailLink";

function FooterSignup() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe/noir-index`, {
        email,
        origin_url: window.location.origin,
        source: "Website - Inner Circle - 15% Welcome Offer",
      });
      localStorage.setItem("fleuris_inner_circle_joined", "true");
      if (res.data?.discount_code) {
        try { localStorage.setItem("fleuris_welcome_code", res.data.discount_code); } catch { /* ignore */ }
      }
      setDone(true);
    } catch {
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="footer-signup">
      <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-[#b8966a] mb-4">
        Join the Inner Circle
      </h4>
      {done ? (
        <p className="font-body text-sm text-[#a19d98] leading-relaxed" data-testid="footer-signup-done">
          Your 15% welcome gift is on its way to your inbox.
        </p>
      ) : (
        <>
          <p className="font-body text-[#a19d98] text-sm leading-relaxed mb-4">
            Be the first to know about exclusive collections, events, and more.
          </p>
          <form onSubmit={submit} className="space-y-3">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full bg-[#161616] border border-[#b8966a]/30 px-4 py-2.5 text-[#f8f5f0] font-body text-sm focus:border-[#b8966a] transition-colors"
              data-testid="footer-signup-email"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full border border-[#b8966a]/40 hover:border-[#b8966a] text-[#b8966a] py-2.5 font-sans text-[10px] tracking-[0.2em] uppercase transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="footer-signup-submit"
            >
              {loading ? <Loader2 className="animate-spin" size={13} /> : "Join the Inner Circle"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

export default function Footer() {
  return (
    <footer 
      className="bg-[#0c0c0c] border-t border-[#b8966a]/10 pt-20 pb-8"
      data-testid="footer"
    >
      <div className="container-custom">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-6">
              <h3 className="font-display text-xl tracking-[0.2em] uppercase">
                <span className="text-[#b8966a]">Fleuris</span> Noir
              </h3>
            </Link>
            <p className="font-body text-[#a19d98] text-sm leading-relaxed mb-6">
              Crafting extraordinary floral experiences in Minneapolis since 2018. 
              Where darkness meets botanical beauty.
            </p>
            <div className="flex gap-4">
              <a
                href="https://instagram.com/fleurisnoir"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#b8966a]/20 flex items-center justify-center text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a] transition-all"
                data-testid="footer-instagram"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://facebook.com/fleurisnoir"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-[#b8966a]/20 flex items-center justify-center text-[#a19d98] hover:text-[#b8966a] hover:border-[#b8966a] transition-all"
                data-testid="footer-facebook"
              >
                <Facebook size={18} />
              </a>
            </div>
            <div className="mt-8">
              <FooterSignup />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-[#b8966a] mb-6">
              Explore
            </h4>
            <ul className="space-y-3">
              {["Collection", "Memberships", "Bespoke", "Events", "Floral Bar"].map((item) => (
                <li key={item}>
                  <Link
                    to={`/${item.toLowerCase().replace(' ', '-')}`}
                    className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-[#b8966a] mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/bespoke" className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
                  Custom Arrangements
                </Link>
              </li>
              <li>
                <Link to="/events" className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
                  Wedding Florals
                </Link>
              </li>
              <li>
                <Link to="/events" className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
                  Event Design
                </Link>
              </li>
              <li>
                <Link to="/floral-bar" className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
                  Floral Bar Pop-Up
                </Link>
              </li>
              <li>
                <Link to="/memberships" className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
                  Subscriptions
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-sans text-xs tracking-[0.2em] uppercase text-[#b8966a] mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-[#b8966a] mt-1 flex-shrink-0" />
                <span className="font-body text-[#a19d98]">
                  Minneapolis, MN<br />Serving the greater metro & beyond
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-[#b8966a] flex-shrink-0" />
                <a href="tel:+16125479260" className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
                  (612) 547-9260
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-[#b8966a] flex-shrink-0" />
                <EmailLink
                  email="hello@fleurisnoir.com"
                  className="font-body text-[#a19d98] hover:text-[#f8f5f0] transition-colors"
                  testId="footer-email"
                >
                  hello@fleurisnoir.com
                </EmailLink>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="divider mb-8"></div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-sans text-xs text-[#a19d98]">
            © {new Date().getFullYear()} Fleuris Noir. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/inquiries" className="font-sans text-xs text-[#a19d98] hover:text-[#f8f5f0] transition-colors">
              Contact
            </Link>
            <span className="text-[#a19d98]/30">|</span>
            <span className="font-sans text-xs text-[#a19d98]">
              Privacy Policy
            </span>
            <span className="text-[#a19d98]/30">|</span>
            <span className="font-sans text-xs text-[#a19d98]">
              Terms of Service
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
